import type React from "react";
import { toast as sonnerToast } from "sonner";
import type { ToastActionElement, ToastType, ToastVariant } from "@/components/flexkit/toast/Toast";
import { ToastContent } from "@/components/flexkit/toast/ToastContent";

type ToasterToast = {
    title: React.ReactNode;
    description?: React.ReactNode;
    action?: ToastActionElement;
    type?: ToastType;
    /** Ignored — sonner handles layout automatically. Kept for backward compatibility. */
    variant?: ToastVariant | "default";
    duration?: number;
    indefinite?: boolean;
    /** Hide the dismiss (X) button. Default: true. */
    dismissible?: boolean;
    onClose?: () => void;
};

function toast(props: ToasterToast) {
    const { title, description, action, type = "default", duration, indefinite, dismissible = true, onClose } = props;

    const resolvedDuration = indefinite ? Infinity : (duration ?? 3000);

    const id = sonnerToast.custom(
        (toastId) => (
            <ToastContent
                id={toastId}
                title={title}
                description={description}
                action={action}
                type={type}
                dismissible={dismissible}
            />
        ),
        {
            duration: resolvedDuration,
            dismissible,
            onDismiss: onClose,
            onAutoClose: onClose,
        },
    );

    return {
        id,
        dismiss: () => sonnerToast.dismiss(id),
    };
}

function useToast() {
    return {
        toast,
        dismiss: (toastId?: string | number) => sonnerToast.dismiss(toastId),
    };
}

function cleanupToastsForTests() {
    sonnerToast.dismiss();
}

export { useToast, toast, cleanupToastsForTests };
